

import { Component, Emit, Prop, Vue, Watch } from "vue-property-decorator";
import NotificationRule from "@/types/sv-data/notification-rule/NotificationRule";
import NotificationRuleConditions from "@/components/notification-templates/NotificationRuleConditions.vue";
import VuePerfectScrollbar from "vue-perfect-scrollbar";
import { cloneDeep, isEqual } from "lodash";
import NotificationRuleCondition from "@/types/sv-data/notification-rule/NotificationRuleCondition";
import { UserPermissions } from "@/store/types";
import { Getter } from "vuex-class";
import notificationTemplatesApi from "@/services/api.notificationTemplate.service";
import NotificationTemplateTypeId from "@/types/sv-data/enums/NotificationTemplateTypeIds";
import vSelect3 from "vselect3";
import NotificationTemplate from "@/types/sv-data/notification-templates/NotificationTemplate";
import NotificationRuleContacts from "@/components/notification-templates/NotificationRuleContacts.vue";
import ShortContact from "@/types/sv-data/users/ShortContact";

@Component({
    components: {
        "notification-rule-conditions": NotificationRuleConditions,
		"scrollbar": VuePerfectScrollbar,
        "v-select-3": vSelect3,
        "notification-rule-contacts": NotificationRuleContacts
	}
})
export default class NotificationRuleSetupModal extends Vue {

	@Prop({ required: true, default: false})
	private showModal: boolean;

    @Prop({type: Object, required: true})
    private rule: NotificationRule;


	@Getter("getPermissions") private permissions: UserPermissions;
	@Getter("getFeature") getFeature: (featureName: string[]) => boolean;
	@Getter getUserTenantGroupId: number;

    private selectedRule: NotificationRule | null = null;
    private conditionsAreValid: boolean = false;
    private submitted: boolean = false;
    private notificationTemplates: NotificationTemplate[] = [];
    private contacts: ShortContact[] = [];

    private async mounted(): Promise<void> {
        this.selectedRule = cloneDeep(this.rule);
        await this.loadNotificationTemplates();
    }

    @Watch("showModal")
    public async onShowModalChanged(newVal: boolean): Promise<void> {
        if (newVal) {
            this.selectedRule = cloneDeep(this.rule);
            this.submitted = false;
            await this.loadNotificationTemplates();
        }
    }

    private get ruleHasContacts(): boolean {
        return this.selectedRule?.emailAddresses?.length > 0
            || this.selectedRule?.userIds?.length > 0
            || this.isContactGroupsEnabled && this.selectedRule?.contactGroupIds?.length > 0;
    }

    private get isContactGroupsEnabled(): boolean {
		return this.getFeature(["Alarms", "SiteMonitor", "Email", "NotificationTemplates", "NotificationGroups"]);
	}

    private get ruleHasTemplate(): boolean {
        return !!this.selectedRule?.notificationTemplateId;
    }

    private get isRuleActionsInvalid(): boolean {
        if (!this.isNotificationTemplateForRulesEnabled) {
            return !this.ruleHasContacts;
        }

        return !this.ruleHasContacts && !this.ruleHasTemplate;
    }

    private get isActionRequired(): boolean {
        return this.isEdit && (!this.isNotificationTemplateForRulesEnabled || this.isRuleActionsInvalid);
    }

    private get ruleIsInvalid(): boolean {
        const hasChanged = !isEqual(this.rule, this.selectedRule);
        return !this.selectedRule?.title || !this.conditionsAreValid || (this.isEdit && !hasChanged) || this.isRuleActionsInvalid;
    }

	private get isReadOnly(): boolean {
		return this.permissions.canViewNotificationRules && !this.permissions.canEditNotificationRules;
	}

    private get isEdit(): boolean {
        return !!this.selectedRule.notificationRuleId;
    }

    @Watch("rule")
    private onRuleChanged(): void {
        this.selectedRule = cloneDeep(this.rule);
        this.submitted = false;
    }

	@Emit("closeSetupModal")
	private closeSetupModal(): void {
        return;
	}

	private async saveCurrentRule(): Promise<void> {
        this.submitted = true;
        if (!this.selectedRule || !this.selectedRule.title || !this.selectedRule.conditions) {
            return;
        }

        this.selectedRule.conditions.forEach((condition: NotificationRuleCondition) => {
            if (typeof condition.notificationRuleConditionId == "string") {
                condition.notificationRuleConditionId = null;
            }
        });

        if (!this.isEdit) {
            this.$emit("addRule", this.selectedRule);
        } else {
            this.$emit("updateRule", this.selectedRule);
        }
	}

    private onUpdateConditions(conditions: NotificationRuleCondition[]): void {
        this.selectedRule.conditions = cloneDeep(conditions);
    }

    private onConditionsValidationUpdated(valid: boolean): void {
        this.conditionsAreValid = valid;
    }

    private get isNotificationTemplatesEnabled(): boolean {
		return this.getFeature(["Alarms", "SiteMonitor", "Email", "NotificationTemplates"]);
	}

    private get isNotificationTemplateForRulesEnabled(): boolean {
        return this.getFeature(["Alarms", "SiteMonitor", "Email", "NotificationTemplates", "NotificationRules", "PredefineTemplate"]);
    }

	private get hasNotificationTemplatePermissions(): boolean {
		return this.permissions.canViewNotificationTemplates || this.permissions.canEditNotificationTemplates;
	}

    private get canUseNotificationTemplates(): boolean {
        return this.isNotificationTemplatesEnabled
            && this.isNotificationTemplateForRulesEnabled
            && this.hasNotificationTemplatePermissions;
    }

    private async loadNotificationTemplates(): Promise<void> {
		if (!this.canUseNotificationTemplates) {
			return;
		}

		try {
			this.notificationTemplates = await notificationTemplatesApi.getAllNotificationTemplates(NotificationTemplateTypeId.Event, this.getUserTenantGroupId);
		} catch (ex) {
			console.error("Unexpected error fetching notification templates: " + ex);
		}
	}

    private async onTemplateChanged(templateId: number | null): Promise<void> {
        if (!this.canUseNotificationTemplates) {
            return;
        }

        if (!this.selectedRule) {
            return;
        }

        this.selectedRule.notificationTemplateId = templateId;
    }

    private onUserIdChanged(userIds: number[]): void {
        if (!this.selectedRule) {
            return;
        }

        this.selectedRule.userIds = userIds;
    }

    private onEmailAddressChanged(emailAddresses: string[]): void {
        if (!this.selectedRule) {
            return;
        }

        this.selectedRule.emailAddresses = emailAddresses;
    }

    private onContactGroupIdsChanged(contactGroupIds: string[]): void {
        if (!this.selectedRule) {
            return;
        }

        this.selectedRule.contactGroupIds = contactGroupIds;
    }
}
